import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebaseConfig';

// Placeholder - update with real Stripe price ID from dashboard
export const STRIPE_PRO_MONTHLY_PRICE_ID = 'price_1T5wxfG14kEsK4I0I1pQ2hq4';
export const PRO_MONTHLY_PRICE = 1.99;

export interface SubscriptionInfo {
  hasActiveSubscription: boolean;
  subscription?: {
    status: string;
    currentPeriodEnd: number;
    subscriptionId: string;
  };
}

export const getSubscriptionInfo = async (userId: string): Promise<SubscriptionInfo> => {
  const fn = httpsCallable<{ userId: string }, SubscriptionInfo>(
    functions,
    'getUserSubscriptionInfo'
  );
  const result = await fn({ userId });
  return result.data;
};

export const createSubscription = async (
  userId: string,
  email: string,
  paymentMethodId: string,
  priceId: string,
  subscriptionPlan: string
) => {
  const fn = httpsCallable<any, { subscriptionId: string }>(
    functions,
    'createSubscription'
  );
  const result = await fn({
    userId,
    email,
    paymentMethodId,
    priceId,
    subscriptionPlan,
  });
  return result.data;
};

export const cancelSubscription = async (subscriptionId: string, userId: string) => {
  const fn = httpsCallable<any, { message: string }>(
    functions,
    'cancelSubscription'
  );
  const result = await fn({ subscriptionId, userId });
  return result.data;
};

export const hasActiveSubscription = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;
  try {
    const info = await getSubscriptionInfo(user.uid);
    return info.hasActiveSubscription;
  } catch {
    return false;
  }
};

const CHECKOUT_LOCK_KEY = 'xtea_sub_checkout_ts';
const CHECKOUT_LOCK_DURATION = 5 * 60 * 1000; // 5 minutes

export const clearSubscriptionCheckoutLock = () => {
  localStorage.removeItem(CHECKOUT_LOCK_KEY);
};

export const createSubscriptionCheckoutSession = async (
  userId: string,
  email: string,
  priceId: string
): Promise<{ sessionId: string; url: string }> => {
  // Prevent rapid re-checkout within 5 minutes (covers webhook delay window)
  const lastCheckout = localStorage.getItem(CHECKOUT_LOCK_KEY);
  if (lastCheckout && Date.now() - parseInt(lastCheckout) < CHECKOUT_LOCK_DURATION) {
    throw new Error('A subscription checkout was recently started. Please wait a few minutes.');
  }

  const alreadySubscribed = await hasActiveSubscription();
  if (alreadySubscribed) {
    clearSubscriptionCheckoutLock();
    throw new Error('You already have an active Pro subscription.');
  }

  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('../firebaseConfig');
  const fn = httpsCallable<any, { sessionId: string; url: string }>(
    functions,
    'createSubscriptionCheckoutSession'
  );
  const result = await fn({ userId, email, priceId });
  localStorage.setItem(CHECKOUT_LOCK_KEY, Date.now().toString());
  return result.data;
};
